import { MinzeElement, Attrs, Reactive, EventListeners } from 'minze'
import MainModule from '../../modules/main'
import { Locale } from '../../modules/locale'
import { Callback } from '../../modules/callback'

Locale.getInstance().registerKeySet('fr', 'lvcAccountNotifications', {
  title: 'Notifications',
  description:
    'Les notifications sont envoyées par courriel. Vous pouvez vous désabonner à tout moment.',
  newsletter: 'Infolettre',
  newsletterDescription: "Recevoir l'infolettre de La Vitrine pour ne rien manquer.",
  favorites: 'Favoris',
  favoritesDescription: 'Recevoir des nouvelles concernant vos artistes et lieux favoris.',
  upcomingEvents: 'Événements',
  upcomingEventsDescription: 'Recevoir des rappels concernant vos prochains événements.',
  changeSavedMsg: 'Vos préférences ont été enregistrées avec succès!',
})

Locale.getInstance().registerKeySet('en', 'lvcAccountNotifications', {
  title: 'Notifications',
  description: 'Notifications are sent via email. You may unsubscribe at any time.',
  newsletter: 'Newsletter',
  newsletterDescription: "I want to subscribe to La Vitrine's newsletter.",
  favorites: 'Favorites',
  favoritesDescription:
    'I wish to receive notifications linked to my favorites artists and venues.',
  upcomingEvents: 'Events',
  upcomingEventsDescription: 'Alerts for upcoming events.',
  changeSavedMsg: 'Your preferences have been saved successfully!',
})

export interface LvcAccountNotifications {
  titleSeparator: boolean
  showToasts: boolean
  categories: any[]
  componentLocation: 'account-notifications' | 'account-complete-profile'
}

export class LvcAccountNotifications extends MinzeElement {
  attrs: Attrs = [
    ['title-separator', false],
    ['show-toasts', true],
    ['component-location', 'account-notifications'],
  ]
  static observedAttributes = ['titleSeparator']
  reactive: Reactive = [
    [
      'categories',
      [
        {
          name: 'newsletter',
          label: Locale.getKey('lvcAccountNotifications.newsletter'),
          description: Locale.getKey('lvcAccountNotifications.newsletterDescription'),
          isActive: false,
        },
        {
          name: 'favorites',
          label: Locale.getKey('lvcAccountNotifications.favorites'),
          description: Locale.getKey('lvcAccountNotifications.favoritesDescription'),
          isActive: false,
        },
        {
          name: 'upcomingEvents',
          label: Locale.getKey('lvcAccountNotifications.upcomingEvents'),
          description: Locale.getKey('lvcAccountNotifications.upcomingEventsDescription'),
          isActive: false,
        },
      ],
    ],
  ]

  private toggleCb: string = Callback.attach(this.onToggleClick.bind(this))

  handleAuthChange() {
    const mainModule = MainModule.getInstance()
    const userData = mainModule.getAuth().getUserData()

    if (userData && userData.profile) {
      const notifications = userData.profile.notifications

      for (const [key, value] of Object.entries(notifications)) {
        const catIndex = this.categories.findIndex(cat => cat.name === key)
        if (catIndex !== -1) this.categories[catIndex].isActive = value
      }

      this.rerender()
    }
  }

  async onToggleClick(): Promise<void> {
    await this.save()
  }

  save = async () => {
    const mainModule = MainModule.getInstance()
    const newsletterElement = this.select('#newsletter')
    const newsletter = newsletterElement?.getAttribute('toggled') === 'true'
    const favoritesElement = this.select('#favorites')
    const favorites = favoritesElement?.getAttribute('toggled') === 'true'
    const upcomingEventsElement = this.select('#upcomingEvents')
    const upcomingEvents = upcomingEventsElement?.getAttribute('toggled') === 'true'

    try {
      // Newsletter has been toggled, track the event
      if (newsletter) {
        await mainModule.analyticsTrack('inscription_infolettre', {
          emplacement:
            this.componentLocation === 'account-complete-profile'
              ? 'création de compte'
              : 'mon compte',
        })
      }
    } catch (error) {
      console.error('Error tracking newsletter subscription:', error)
    }

    await mainModule.getAuth().saveProfile('profile-notification', {
      newsletter,
      favorites,
      upcomingEvents,
    })
    if (this.showToasts)
      mainModule.showToastAlert(Locale.getKey('lvcAccountNotifications.changeSavedMsg'))
  }

  async onReady(): Promise<void> {
    this.handleAuthChange()
  }

  private renderCategory(category: any) {
    let { name, label, description, isActive } = category

    return /* html */ `
      <lvc-hand-drawn-box variant="button1">
        <div class="account-notifications-category">
          <div>
            <div class="account-notifications-category-label">${label}</div>
            <div class="account-notifications-category-description">${description}</div>
          </div>
          <div class="account-notifications-category-toggle">
            <lvc-toggle id=${name} class="toggle" on-click="${this.toggleCb}" initial-toggled="${isActive}"></lvc-toggle>
          </div>
        </div>
      </lvc-hand-drawn-box>
    `
  }

  html = () => `
    <div class="account-notifications">
      <lvc-account-header
        label="${Locale.getKey('lvcAccountNotifications.title')}"
        description="${Locale.getKey('lvcAccountNotifications.description')}"
        separator="${this.titleSeparator}"
      >
        <lvc-account-bell-icon width="44" height="44" slot="icon"></lvc-account-bell-icon>
      </lvc-account-header>
      <div class="account-notifications-categories">
        ${this.categories.map(category => this.renderCategory(category)).join('')}
      </div>
    </div>
  `

  eventListeners: EventListeners = [
    [window, 'lvc:user_state_changed', this.handleAuthChange.bind(this)],
  ]

  css = () => `
    :host {}

    .account-notifications-categories {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .account-notifications-category {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 10px 10px;
    }

    .account-notifications-category-label {
      margin-bottom: 5px;
      color: #1E007D;
      font-size: 1.125em;
      font-weight: 800;
      line-height: 120%;
      text-transform: uppercase;
    }

    .account-notifications-category-description {
      color: #1E007D;
      font-size: 0.750em;
      font-weight: 500;
      line-height: 120%;
    }

    .account-notifications-category-toggle {}

    @media only screen and (min-width: 992px) {
      .account-notifications-categories {
        gap: 15 px;
      }

      .account-notifications-category {
        padding: 10px 20px;
      }

      .account-notifications-category-label {
        font-size: 1.375em;
      }

      .account-notifications-category-description {
        font-size: 0.875em;
      }

      .account-notifications-category-toggle {}
    }
  `
}
